import { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import {
  Button,
  DateInput,
  FileUpload,
  FieldRow,
  IconButton,
  PlusIcon,
  Selector,
  TextArea,
  TextInput,
  type SelectorOption,
} from "../../components/ui";
import {
  ErrorState,
  InlineNotice,
  Loading,
  PageHeader,
} from "../../components/WorkspacePrimitives";
import { hideChatAndFastTrack } from "../../lib/featureFlags";
import {
  DEFAULT_AGENT_START_DATE,
  useAgentCompanies,
  useAgentConfiguration,
  useAgentModelVersions,
  useAttachAgentRunDraftInput,
  useCreateAgentRun,
  useAgentRun,
  useAgentRuns,
  useDetachAgentRunDraftInput,
  useStartAgentRunDraft,
  useUpdateAgentRunDraft,
  useUploadAgentInput,
  type AgentConfiguration,
  type AgentInputMetadata,
  type AgentInputMode,
  type AgentModel,
  type AgentRun,
  type AgentRunInput,
  type AgentRunMode,
  type AgentRunParameters,
  type AgentVisibility,
} from "../api/e2e";
import CompanyAutocomplete from "../components/CompanyAutocomplete";
import CreateCompanyModal from "../components/CreateCompanyModal";
import { AGENT_VISIBILITY_OPTIONS } from "../components/AgentVisibilityControl";
import { companyRunPath } from "../lib/runRoutes";
import { validateAgentInputFile } from "../lib/inputFiles";
import { shortStamp } from "../lib/format";

interface InputMetadataDraft {
  description: string;
  sourceUrl: string;
  releaseDate: string;
}

type StartingPoint = "fresh" | "iterate";
type AgentServiceTier = "standard" | "fast";

const SERVICE_TIER_OPTIONS: readonly SelectorOption<AgentServiceTier>[] = [
  {
    value: "standard",
    label: "Standard",
    secondary: "Standard cost and speed",
  },
  {
    value: "fast",
    label: "Fast",
    secondary: "2× cost · Approximately 2× faster",
  },
];

const RUN_MODE_OPTIONS: readonly SelectorOption<AgentRunMode>[] = [
  {
    value: "standard",
    label: "Standard",
    secondary: "Full workflow · Selectable service tier",
  },
  {
    value: "fast_track",
    label: "Fast Track",
    secondary: "Three-hour streamlined workflow",
  },
];

export function runParametersForSelection(
  config: Pick<
    AgentConfiguration,
    "default_fast" | "default_timeout_seconds" | "fast_track_defaults"
  >,
  runMode: AgentRunMode,
  inputMode: AgentInputMode,
  modelSelection: {
    model: string;
    reasoning_effort: string;
    fast?: boolean;
  } = {
    model: "gpt-5.6-sol",
    reasoning_effort: "",
  },
): AgentRunParameters {
  const selection = {
    model: modelSelection.model,
    ...(modelSelection.reasoning_effort
      ? { reasoning_effort: modelSelection.reasoning_effort }
      : {}),
  };
  if (runMode === "fast_track") {
    return {
      run_mode: "fast_track",
      input_mode: inputMode,
      ...selection,
      ...config.fast_track_defaults,
      fast: modelSelection.fast ?? config.fast_track_defaults.fast,
    };
  }
  return {
    run_mode: "standard",
    input_mode: inputMode,
    ...selection,
    fast: modelSelection.fast ?? config.default_fast,
    timeout_seconds: config.default_timeout_seconds,
  };
}

function modelVersionOptions(
  models: readonly AgentModel[],
): readonly SelectorOption<string>[] {
  return models.map((model) => ({
    value: model.id,
    label: `Version ${model.sequence} · ${shortStamp(model.published_at)}`,
    secondary: model.hypothesis.trim() || "No prior hypothesis.",
  }));
}

function fileKey(
  companyId: string,
  role: string,
  position: number,
  file: File,
  metadata: InputMetadataDraft,
): string {
  return [
    companyId,
    role,
    position,
    file.name,
    file.size,
    file.lastModified,
    metadata.description,
    metadata.sourceUrl,
    metadata.releaseDate,
  ].join(":");
}

function metadataKey(role: string, position: number, file: File): string {
  return [role, position, file.name, file.size, file.lastModified].join(":");
}

function defaultMetadata(file: File): InputMetadataDraft {
  return { description: file.name, sourceUrl: "", releaseDate: "" };
}

export function latestCanalystInputForCompany(
  runs: readonly AgentRun[],
  companyId: string,
): AgentRunInput | null {
  let latest: { input: AgentRunInput; createdAt: string } | null = null;
  for (const run of runs) {
    if (run.company_id !== companyId) continue;
    const input = run.inputs.find((candidate) => candidate.role === "canalyst");
    if (input && (!latest || run.created_at > latest.createdAt)) {
      latest = { input, createdAt: run.created_at };
    }
  }
  return latest?.input ?? null;
}

export default function AgentLaunchPage({
  modal = false,
  draftId: suppliedDraftId,
  initialCompanyId,
  initialBaseModelVersionId,
  onClose,
}: {
  modal?: boolean;
  draftId?: string;
  initialCompanyId?: string;
  initialBaseModelVersionId?: string;
  onClose?: () => void;
}) {
  const restrictedProductionUi = hideChatAndFastTrack();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftId = suppliedDraftId ?? searchParams.get("draft") ?? "";
  const requestedCompanyId =
    initialCompanyId ?? searchParams.get("company") ?? "";
  const requestedBaseModelVersionId =
    initialBaseModelVersionId ?? searchParams.get("base_model") ?? "";
  const editingDraft = draftId !== "";
  const openingIteration = !editingDraft && requestedBaseModelVersionId !== "";
  const focusedIterationModal = modal && openingIteration;
  const configuration = useAgentConfiguration();
  const companies = useAgentCompanies();
  const runs = useAgentRuns();
  const draft = useAgentRun(draftId);
  const createRun = useCreateAgentRun();
  const updateDraft = useUpdateAgentRunDraft();
  const attachDraftInput = useAttachAgentRunDraftInput();
  const detachDraftInput = useDetachAgentRunDraftInput();
  const startDraft = useStartAgentRunDraft();
  const upload = useUploadAgentInput();
  const uploadedIds = useRef(new Map<string, string>());
  const autoPrefilledBaseModelId = useRef("");
  const [selectedCompanyId, setSelectedCompanyId] =
    useState(requestedCompanyId);
  const [companySelectionCleared, setCompanySelectionCleared] = useState(false);
  const [companySearch, setCompanySearch] = useState("");
  const [creatingCompany, setCreatingCompany] = useState(false);
  const [baseModelVersionId, setBaseModelVersionId] = useState(
    requestedBaseModelVersionId,
  );
  const [startingPoint, setStartingPoint] = useState<StartingPoint>(
    requestedBaseModelVersionId ? "iterate" : "fresh",
  );
  const [hypothesis, setHypothesis] = useState("");
  const [iterationMessage, setIterationMessage] = useState("");
  const [runMode, setRunMode] = useState<AgentRunMode>("standard");
  const [principalModelId, setPrincipalModelId] = useState("");
  const [reasoningEffort, setReasoningEffort] = useState("");
  const [serviceTier, setServiceTier] = useState<AgentServiceTier | null>(null);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [visibility, setVisibility] = useState<AgentVisibility>("private");
  const [agentVersion, setAgentVersion] = useState("");
  const [canalyst, setCanalyst] = useState<File | null>(null);
  const [excludedCanalystInputId, setExcludedCanalystInputId] = useState("");
  const [documents, setDocuments] = useState<File[]>([]);
  const [inputMetadata, setInputMetadata] = useState<
    Record<string, InputMetadataDraft>
  >({});
  const [progress, setProgress] = useState<string | null>(null);
  const configuredAgentVersion =
    configuration.data?.default_agent_version ??
    configuration.data?.agent_versions?.[0]?.version ??
    "";
  const selectedRunMode =
    editingDraft && !restrictedProductionUi ? runMode : "standard";
  const modelPresets = configuration.data?.models ?? [];
  const requestedModelId =
    principalModelId ||
    configuration.data?.default_model ||
    modelPresets[0]?.id ||
    "gpt-5.6-sol";
  const selectedModelPreset =
    modelPresets.find((preset) => preset.id === requestedModelId) ??
    modelPresets.find(
      (preset) => preset.id === configuration.data?.default_model,
    ) ??
    modelPresets[0];
  const selectedModelId = selectedModelPreset?.id ?? requestedModelId;
  const selectedReasoningEffort =
    selectedModelPreset?.reasoning_efforts.includes(reasoningEffort)
      ? reasoningEffort
      : (selectedModelPreset?.default_reasoning_effort ?? "");
  const selectedFastService =
    selectedModelPreset?.supports_fast !== false &&
    (selectedRunMode === "fast_track" ||
      (serviceTier === null
        ? configuration.data?.default_fast !== false
        : serviceTier === "fast"));
  const selectedAgentVersion = editingDraft
    ? agentVersion
    : configuredAgentVersion;
  const effectiveCompanyId =
    draft.data?.company_id ||
    selectedCompanyId ||
    (!companySelectionCleared ? companies.data?.[0]?.id : "") ||
    "";
  const modelVersions = useAgentModelVersions(effectiveCompanyId);
  const modelVersionChoices = modelVersionOptions(modelVersions.data ?? []);
  const baseModel = modelVersions.data?.find(
    (model) => model.id === baseModelVersionId,
  );
  const iterating = startingPoint === "iterate";
  let iterationStartingPointDescription =
    "Choose a published model and describe what should change.";
  if (modelVersions.isPending) {
    iterationStartingPointDescription = "Loading published models…";
  } else if ((modelVersions.data?.length ?? 0) === 0) {
    iterationStartingPointDescription =
      "No published models are available for this ticker yet.";
  }

  useEffect(() => {
    if (draft.data?.state.kind === "draft") {
      setHypothesis(draft.data.hypothesis);
      setIterationMessage(draft.data.iteration_message ?? "");
      setRunMode(draft.data.parameters.run_mode);
      setPrincipalModelId(draft.data.parameters.model);
      setReasoningEffort(draft.data.parameters.reasoning_effort ?? "");
      setServiceTier(draft.data.parameters.fast ? "fast" : "standard");
      setStartDate(
        draft.data.parameters.start_date ?? DEFAULT_AGENT_START_DATE,
      );
      setAgentVersion(draft.data.agent_version?.version ?? "");
      setBaseModelVersionId(draft.data.base_model?.model_version_id ?? "");
      setStartingPoint(
        draft.data.base_model?.model_version_id ? "iterate" : "fresh",
      );
    }
  }, [
    draft.data?.agent_version?.version,
    draft.data?.base_model?.model_version_id,
    draft.data?.hypothesis,
    draft.data?.iteration_message,
    draft.data?.parameters.run_mode,
    draft.data?.parameters.model,
    draft.data?.parameters.reasoning_effort,
    draft.data?.parameters.fast,
    draft.data?.parameters.start_date,
    draft.data?.state.kind,
  ]);

  useEffect(() => {
    if (
      editingDraft ||
      !baseModel ||
      autoPrefilledBaseModelId.current === baseModel.id
    ) {
      return;
    }
    autoPrefilledBaseModelId.current = baseModel.id;
    setHypothesis((current) => current.trim() || baseModel.hypothesis);
  }, [baseModel, editingDraft]);

  useEffect(() => {
    if (!editingDraft || agentVersion !== "") return;
    if (!draft.data || draft.data.state.kind !== "draft") return;
    if (draft.data.agent_version) return;
    if (configuredAgentVersion) setAgentVersion(configuredAgentVersion);
  }, [agentVersion, configuredAgentVersion, draft.data, editingDraft]);

  if (
    (configuration.isPending && !configuration.data) ||
    (companies.isPending && !companies.data) ||
    (editingDraft && draft.isPending && !draft.data)
  ) {
    return <Loading label="Loading launch parameters…" />;
  }
  if (configuration.error && !configuration.data) {
    return (
      <ErrorState
        error={configuration.error}
        onRetry={() => configuration.refetch()}
      />
    );
  }
  if (companies.error && !companies.data) {
    return (
      <ErrorState error={companies.error} onRetry={() => companies.refetch()} />
    );
  }
  if (editingDraft && draft.error && !draft.data) {
    return <ErrorState error={draft.error} onRetry={() => draft.refetch()} />;
  }
  const config = configuration.data;
  if (!config) return null;
  const defaultStartDate =
    config.default_start_date ?? DEFAULT_AGENT_START_DATE;
  const selectedStartDate = startDate ?? defaultStartDate;
  const latestStartDate = new Date().toISOString().slice(0, 10);
  const startDateAllowed =
    config.start_date_selection_enabled === true ||
    selectedStartDate === defaultStartDate;
  const startDateValid =
    startDateAllowed &&
    /^\d{4}-\d{2}-\d{2}$/.test(selectedStartDate) &&
    selectedStartDate >= DEFAULT_AGENT_START_DATE &&
    selectedStartDate <= latestStartDate &&
    Number.isFinite(Date.parse(`${selectedStartDate}T00:00:00Z`)) &&
    new Date(`${selectedStartDate}T00:00:00Z`).toISOString().slice(0, 10) ===
      selectedStartDate;
  if (!config.iteration_enabled && (iterating || draft.data?.base_model)) {
    return (
      <div
        className={
          modal ? "agent-launch-modal-content" : "page page--workspace"
        }
      >
        <InlineNotice tone="warning">
          Iteration is disabled in this environment. Create a fresh run instead.
          Existing iterative drafts cannot be edited or started.
        </InlineNotice>
        {modal && onClose ? (
          <Button tone="quiet" onClick={onClose}>
            Close
          </Button>
        ) : (
          <Link to={`/companies/${effectiveCompanyId}`}>Return to company</Link>
        )}
      </div>
    );
  }
  const companyOptions = companies.data ?? [];
  const draftRun = editingDraft ? draft.data : undefined;
  const companyId = effectiveCompanyId;
  const selectedCompany = companyOptions.find(
    (company) => company.id === companyId,
  );
  const latestCanalystInput = editingDraft
    ? null
    : latestCanalystInputForCompany(runs.data ?? [], companyId);
  const researchEnabled = config.input_modes.includes("research");
  const defaultCanalystInput =
    researchEnabled && latestCanalystInput?.id === excludedCanalystInputId
      ? null
      : latestCanalystInput;
  const newRunHasCanalyst = canalyst !== null || defaultCanalystInput !== null;
  const hasCanalyst = editingDraft
    ? canalyst !== null ||
      draftRun?.inputs.some((input) => input.role === "canalyst") === true
    : newRunHasCanalyst;
  const selectedInputMode: AgentInputMode =
    hasCanalyst || !researchEnabled ? "canalyst" : "research";
  const accepts = config.supported_extensions.join(",");
  const draftState =
    draftRun?.state.kind === "draft" ? draftRun.state : undefined;
  const validDraft = draftState ? draftRun : undefined;
  const availableAgentVersions = config.agent_versions ?? [];
  const pinnedDraftVersion = validDraft?.agent_version;
  const selectableAgentVersions =
    pinnedDraftVersion &&
    !availableAgentVersions.some(
      (version) => version.version === pinnedDraftVersion.version,
    )
      ? [...availableAgentVersions, pinnedDraftVersion]
      : availableAgentVersions;
  const defaultAgentVersion = availableAgentVersions.find(
    (version) => version.version === config.default_agent_version,
  );
  const agentVersionOptions: readonly SelectorOption<string>[] =
    selectableAgentVersions.length === 0
      ? [
          {
            value: "",
            label: "Latest",
            secondary: "No registered Agent version is available",
          },
        ]
      : selectableAgentVersions.map((version) => {
          const commitSubject =
            version.source_commit_message.split(/\r?\n/, 1)[0]?.trim() ||
            "Commit message unavailable";
          const sourceContext = version.source_pull_request_number
            ? `PR #${version.source_pull_request_number} · ${version.source_commit_sha.slice(0, 8)}`
            : version.source_commit_sha.slice(0, 8);
          return {
            value: version.version,
            label:
              version.version === defaultAgentVersion?.version
                ? `${version.version} · Latest`
                : version.version,
            secondary: `${commitSubject} · ${sourceContext}`,
          };
        });
  const creatingFirstCompany = companyOptions.length === 0;
  const busy =
    progress !== null ||
    createRun.isPending ||
    upload.isPending ||
    updateDraft.isPending ||
    attachDraftInput.isPending ||
    detachDraftInput.isPending ||
    startDraft.isPending;
  let heading = "Launch a new run";
  let description =
    "Choose a company, then start fresh or build on a published model.";
  let launchLabel = "Launch run";
  if (editingDraft) {
    heading = "Edit draft run";
    description =
      "Review the draft, update its instructions, and launch it when its inputs are ready.";
    launchLabel = "Launch draft";
  } else if (openingIteration) {
    heading = "Iterate on this model";
    description =
      "Describe what should change and add any new evidence for this iteration.";
    launchLabel = "Launch iteration";
  }
  if (busy) launchLabel = "Working…";
  const iterationFieldsReady =
    !iterating ||
    (baseModelVersionId !== "" &&
      (focusedIterationModal || hypothesis.trim() !== "") &&
      iterationMessage.trim() !== "");
  const draftCanLaunch =
    (hasCanalyst || researchEnabled) && iterationFieldsReady;
  const draftChanged =
    validDraft !== undefined &&
    (hypothesis.trim() !== validDraft.hypothesis ||
      iterationMessage.trim() !== (validDraft.iteration_message ?? "") ||
      selectedInputMode !== validDraft.parameters.input_mode ||
      runMode !== validDraft.parameters.run_mode ||
      selectedModelId !== validDraft.parameters.model ||
      selectedReasoningEffort !==
        (validDraft.parameters.reasoning_effort ?? "") ||
      selectedFastService !== validDraft.parameters.fast ||
      selectedStartDate !==
        (validDraft.parameters.start_date ?? DEFAULT_AGENT_START_DATE) ||
      agentVersion !== (validDraft.agent_version?.version ?? "") ||
      baseModelVersionId !== (validDraft.base_model?.model_version_id ?? "") ||
      canalyst !== null ||
      documents.length > 0);
  let draftWarning: string | null = null;
  if (draftState && !draftCanLaunch) {
    if (iterating && !baseModelVersionId) {
      draftWarning =
        "Choose a published model before launching this iteration.";
    } else if (iterating && !hypothesis.trim()) {
      draftWarning =
        "Add a modeling hypothesis before launching this iteration.";
    } else if (iterating && !iterationMessage.trim()) {
      draftWarning = "Add an iteration message before launching this draft.";
    } else {
      draftWarning =
        "Attach a Canalyst workbook before launching; instruction changes can still be saved here.";
    }
  }

  const selectedParameters = (): AgentRunParameters => ({
    ...runParametersForSelection(config, selectedRunMode, selectedInputMode, {
      model: selectedModelId,
      reasoning_effort: selectedReasoningEffort,
      fast: selectedFastService,
    }),
    ...(config.start_date_selection_enabled === true
      ? { start_date: selectedStartDate }
      : {}),
  });

  const resetFiles = () => {
    setCanalyst(null);
    setDocuments([]);
    setInputMetadata({});
    uploadedIds.current.clear();
  };

  const metadataFor = (
    role: "canalyst" | "other_document",
    position: number,
    file: File,
  ): InputMetadataDraft =>
    inputMetadata[metadataKey(role, position, file)] ?? defaultMetadata(file);

  const updateMetadata = (
    role: "canalyst" | "other_document",
    position: number,
    file: File,
    field: keyof InputMetadataDraft,
    value: string,
  ) => {
    const key = metadataKey(role, position, file);
    setInputMetadata((current) => ({
      ...current,
      [key]: { ...(current[key] ?? defaultMetadata(file)), [field]: value },
    }));
  };

  const validate = (): string | null => {
    if (!companyId) return "Create or select a company first.";
    if (availableAgentVersions.length === 0 && !pinnedDraftVersion) {
      return "No Agent image version is available.";
    }
    if (iterating && !baseModelVersionId) {
      return "Choose a published model to iterate on.";
    }
    if (iterating && !focusedIterationModal && !hypothesis.trim()) {
      return "Enter the modeling hypothesis for this iteration.";
    }
    if (iterating && !iterationMessage.trim()) {
      return "Describe what should change in the iteration message.";
    }
    if (iterating && modelVersions.data && !baseModel) {
      return "The selected starting model is no longer available.";
    }
    if (!newRunHasCanalyst && !researchEnabled)
      return "Upload a Canalyst workbook before launching the run.";
    if (canalyst) {
      const canalystProblem = validateAgentInputFile(canalyst, "canalyst");
      if (canalystProblem) return canalystProblem;
    }
    for (const file of documents) {
      const problem = validateAgentInputFile(file, "other_document");
      if (problem) return problem;
    }
    return null;
  };

  const ensureUploaded = async (
    file: File,
    role: "canalyst" | "other_document",
    position: number,
  ): Promise<string> => {
    const metadata = metadataFor(role, position, file);
    const key = fileKey(companyId, role, position, file, metadata);
    const existing = uploadedIds.current.get(key);
    if (existing) return existing;
    setProgress(`Hashing and uploading ${file.name}…`);
    const requestMetadata: AgentInputMetadata = {
      description: metadata.description,
      sourceUrl: metadata.sourceUrl,
      releaseDate: metadata.releaseDate,
    };
    const result = await upload.mutateAsync({
      companyId,
      file,
      role,
      metadata: requestMetadata,
    });
    if (result.state !== "ready") {
      throw new Error(
        result.rejection_reason ?? `${file.name} was rejected after upload.`,
      );
    }
    uploadedIds.current.set(key, result.id);
    return result.id;
  };

  const persistDraft = async () => {
    if (!validDraft)
      throw new Error("This run is no longer an editable draft.");
    if (!startDateValid)
      throw new Error("Choose a valid historical start date.");
    const attachedInputs = [...validDraft.inputs];
    const attachedInputIds = new Set(attachedInputs.map((input) => input.id));

    const previousAgentVersion = validDraft.agent_version?.version ?? "";
    await updateDraft.mutateAsync({
      runId: validDraft.id,
      hypothesis,
      iterationMessage: iterating ? iterationMessage : null,
      parameters: selectedParameters(),
      ...(agentVersion !== previousAgentVersion
        ? { agentVersion: agentVersion || null }
        : {}),
      baseModelVersionId: iterating ? baseModelVersionId || null : null,
    });

    if (canalyst) {
      const inputId = await ensureUploaded(canalyst, "canalyst", 0);
      for (const input of attachedInputs.filter(
        (candidate) =>
          candidate.role === "canalyst" && candidate.id !== inputId,
      )) {
        await detachDraftInput.mutateAsync({
          runId: validDraft.id,
          inputId: input.id,
        });
        attachedInputIds.delete(input.id);
      }
      if (!attachedInputIds.has(inputId)) {
        await attachDraftInput.mutateAsync({ runId: validDraft.id, inputId });
        attachedInputIds.add(inputId);
      }
    }

    for (const [index, file] of documents.entries()) {
      const inputId = await ensureUploaded(file, "other_document", index);
      if (!attachedInputIds.has(inputId)) {
        await attachDraftInput.mutateAsync({ runId: validDraft.id, inputId });
        attachedInputIds.add(inputId);
      }
    }

    resetFiles();
  };

  const submit = async () => {
    if (editingDraft) {
      if (!validDraft) {
        toast.error("This run is no longer an editable draft.");
        return;
      }
      if (!draftCanLaunch) {
        toast.error("Complete the draft inputs before launching the run.");
        return;
      }
      try {
        setProgress("Saving draft changes…");
        await persistDraft();
        setProgress("Launching draft run…");
        const run = await startDraft.mutateAsync(validDraft.id);
        toast.success(`${run.ticker} run launched.`);
        onClose?.();
        navigate(companyRunPath(run));
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : "The draft launch failed.",
        );
      } finally {
        setProgress(null);
      }
      return;
    }

    const problem = validate();
    if (problem) {
      toast.error(problem);
      return;
    }
    try {
      const canalystInputId = canalyst
        ? await ensureUploaded(canalyst, "canalyst", 0)
        : (defaultCanalystInput?.id ?? null);
      const otherInputIds: string[] = [];
      for (const [index, file] of documents.entries()) {
        otherInputIds.push(await ensureUploaded(file, "other_document", index));
      }
      setProgress("Scheduling one independent run…");
      const run = await createRun.mutateAsync({
        company_id: companyId,
        visibility,
        ...(selectedAgentVersion
          ? { agent_version: selectedAgentVersion }
          : {}),
        ...(baseModelVersionId
          ? { base_model_version_id: baseModelVersionId }
          : {}),
        hypothesis,
        ...(baseModelVersionId ? { iteration_message: iterationMessage } : {}),
        parameters: selectedParameters(),
        canalyst_input_id: canalystInputId,
        other_input_ids: otherInputIds,
        correlation: { source: "browser" },
      });
      toast.success(`${run.ticker} run scheduled.`);
      navigate(companyRunPath(run));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "The launch failed.",
      );
    } finally {
      setProgress(null);
    }
  };

  const saveDraft = async () => {
    if (!validDraft || !draftChanged) return;
    try {
      setProgress("Saving draft changes…");
      await persistDraft();
      toast.success("Draft run updated.");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "The draft update failed.",
      );
    } finally {
      setProgress(null);
    }
  };

  return (
    <div
      className={modal ? "agent-launch-modal-content" : "page page--workspace"}
    >
      {modal ? (
        <header className="agent-launch-heading">
          <span className="agent-launch-eyebrow">
            {editingDraft
              ? "Draft run"
              : openingIteration
                ? "Published model"
                : "New modeling run"}
          </span>
          <h1>{heading}</h1>
          <p>{description}</p>
        </header>
      ) : (
        <PageHeader className="agent-launch-heading">
          <Link to="/runs" className="back-link">
            ← Runs
          </Link>
          <h1>{heading}</h1>
          <p>{description}</p>
        </PageHeader>
      )}

      <div className="agent-launch-scroll">
        {(companies.error || configuration.error || runs.error) && (
          <InlineNotice tone="warning">
            Some launch context could not refresh. The latest cached values
            remain available.
          </InlineNotice>
        )}

        {editingDraft && !validDraft && (
          <InlineNotice tone="warning">
            This run is no longer an editable draft. Return to Runs to view its
            current state.
          </InlineNotice>
        )}

        {draftWarning && (
          <InlineNotice tone="warning">{draftWarning}</InlineNotice>
        )}

        <section className="agent-launch-section" aria-label="Run details">
          {creatingFirstCompany && (
            <InlineNotice tone="warning">
              No companies exist yet. Create one here to continue.
            </InlineNotice>
          )}

          {!focusedIterationModal && (
            <div className="agent-company-picker agent-launch-card">
              <CompanyAutocomplete
                companies={companyOptions}
                value={companySearch}
                onChange={setCompanySearch}
                onClear={() => {
                  setSelectedCompanyId("");
                  setCompanySelectionCleared(true);
                  setStartingPoint("fresh");
                  setBaseModelVersionId("");
                  setIterationMessage("");
                  autoPrefilledBaseModelId.current = "";
                  resetFiles();
                }}
                onSelect={(company) => {
                  setSelectedCompanyId(company.id);
                  setCompanySelectionCleared(false);
                  setCompanySearch("");
                  setStartingPoint("fresh");
                  setBaseModelVersionId("");
                  setIterationMessage("");
                  autoPrefilledBaseModelId.current = "";
                  resetFiles();
                }}
                selectedCompany={selectedCompany}
                label="Company"
                disabled={busy || editingDraft}
              />
              {!editingDraft && (
                <IconButton
                  tone="secondary"
                  size="sm"
                  containerClassName="agent-company-picker__create"
                  label="Create company"
                  tooltip="Add new ticker"
                  icon={<PlusIcon />}
                  disabled={busy}
                  onClick={() => setCreatingCompany(true)}
                />
              )}
            </div>
          )}

          {!creatingFirstCompany && (
            <fieldset
              key={companyId}
              className="agent-launch-card"
              disabled={busy}
            >
              <legend className="sr-only">Run details</legend>
              <div className="agent-field-grid">
                {config.iteration_enabled &&
                  !restrictedProductionUi &&
                  !focusedIterationModal && (
                    <div className="agent-starting-point agent-field-wide">
                      <span className="agent-starting-point__label">
                        Starting point
                      </span>
                      <div
                        className="agent-starting-point__choices"
                        role="group"
                        aria-label="Starting point"
                      >
                        <button
                          type="button"
                          className="agent-starting-point__choice"
                          aria-pressed={!iterating}
                          onClick={() => {
                            setStartingPoint("fresh");
                            setBaseModelVersionId("");
                            setIterationMessage("");
                            autoPrefilledBaseModelId.current = "";
                          }}
                        >
                          <strong>Start fresh</strong>
                          <span>
                            Generate a model for this ticker from scratch.
                          </span>
                        </button>
                        <button
                          type="button"
                          className="agent-starting-point__choice"
                          aria-pressed={iterating}
                          disabled={
                            modelVersions.isPending ||
                            (modelVersions.data?.length ?? 0) === 0
                          }
                          onClick={() => setStartingPoint("iterate")}
                        >
                          <strong>Iterate on existing model</strong>
                          <span>{iterationStartingPointDescription}</span>
                        </button>
                      </div>
                    </div>
                  )}
                {!focusedIterationModal && iterating && (
                  <Selector
                    label="Model to iterate on"
                    options={modelVersionChoices}
                    value={baseModelVersionId}
                    placeholder="Select a published model…"
                    onValueChange={(nextBaseModelVersionId) => {
                      setBaseModelVersionId(nextBaseModelVersionId);
                      if (!nextBaseModelVersionId) {
                        setIterationMessage("");
                        autoPrefilledBaseModelId.current = "";
                        return;
                      }
                      const nextBaseModel = modelVersions.data?.find(
                        (model) => model.id === nextBaseModelVersionId,
                      );
                      if (nextBaseModel) {
                        autoPrefilledBaseModelId.current = nextBaseModel.id;
                        setHypothesis(nextBaseModel.hypothesis);
                      }
                    }}
                    disabled={busy || modelVersions.isPending}
                    className="agent-field-wide"
                  />
                )}
                {modelPresets.length > 1 && (
                  <Selector
                    label="Principal model"
                    options={modelPresets.map((preset) => ({
                      value: preset.id,
                      label: preset.label,
                      secondary: preset.supports_fast
                        ? "Fast service available"
                        : "Standard service tier",
                    }))}
                    value={selectedModelId}
                    onValueChange={(nextModelId) => {
                      setPrincipalModelId(nextModelId);
                      setReasoningEffort("");
                      if (
                        modelPresets.find((preset) => preset.id === nextModelId)
                          ?.supports_fast === false
                      ) {
                        setServiceTier("standard");
                      }
                    }}
                    disabled={busy}
                  />
                )}
                {(selectedModelPreset?.reasoning_efforts.length ?? 0) > 1 && (
                  <Selector
                    label="Reasoning effort"
                    options={(selectedModelPreset?.reasoning_efforts ?? []).map(
                      (effort) => ({
                        value: effort,
                        label: effort,
                        secondary:
                          effort ===
                          selectedModelPreset?.default_reasoning_effort
                            ? "Default"
                            : undefined,
                      }),
                    )}
                    value={selectedReasoningEffort}
                    onValueChange={setReasoningEffort}
                    disabled={busy}
                  />
                )}
                <FieldRow label="Service tier" layout="stacked">
                  <Selector
                    ariaLabel="Service tier"
                    options={SERVICE_TIER_OPTIONS}
                    value={selectedFastService ? "fast" : "standard"}
                    onValueChange={setServiceTier}
                    disabled={
                      busy ||
                      selectedModelPreset?.supports_fast === false ||
                      selectedRunMode === "fast_track"
                    }
                  />
                </FieldRow>
                {config.start_date_selection_enabled === true && (
                  <FieldRow label="Start date" layout="stacked">
                    <DateInput
                      aria-label="Start date"
                      helperText="Jan 1, 2005 – today (UTC)"
                      min={DEFAULT_AGENT_START_DATE}
                      max={latestStartDate}
                      value={selectedStartDate}
                      onValueChange={setStartDate}
                      disabled={busy}
                      aria-invalid={!startDateValid}
                    />
                  </FieldRow>
                )}
                {!startDateAllowed && (
                  <InlineNotice tone="warning">
                    This draft has a custom start date, which is disabled in
                    this environment. Create a new run instead.
                  </InlineNotice>
                )}
                {editingDraft && (
                  <>
                    {!restrictedProductionUi && (
                      <Selector
                        label="Execution"
                        options={RUN_MODE_OPTIONS}
                        value={runMode}
                        onValueChange={setRunMode}
                      />
                    )}
                    <Selector
                      label="Agent version"
                      options={agentVersionOptions}
                      value={agentVersion}
                      onValueChange={setAgentVersion}
                      disabled={
                        busy ||
                        (availableAgentVersions.length === 0 &&
                          !pinnedDraftVersion)
                      }
                    />
                  </>
                )}
                {!editingDraft && (
                  <Selector
                    label="Visibility"
                    options={AGENT_VISIBILITY_OPTIONS}
                    value={visibility}
                    onValueChange={setVisibility}
                    disabled={busy}
                  />
                )}
                {!focusedIterationModal && (
                  <TextArea
                    className="agent-field-wide"
                    label={
                      iterating
                        ? "Modeling hypothesis · required"
                        : "Modeling hypothesis · optional"
                    }
                    value={hypothesis}
                    rows={3}
                    maxLength={2000}
                    disabled={busy || (editingDraft && !validDraft)}
                    onValueChange={setHypothesis}
                  />
                )}
                {iterating && (
                  <TextArea
                    className="agent-field-wide"
                    label="Iteration message · required"
                    value={iterationMessage}
                    rows={3}
                    maxLength={2000}
                    disabled={busy || (editingDraft && !validDraft)}
                    onValueChange={setIterationMessage}
                  />
                )}
                {validDraft && validDraft.inputs.length > 0 && (
                  <div className="agent-field-wide agent-draft-inputs">
                    <span>Attached files</span>
                    {validDraft.inputs.map((input) => (
                      <div key={input.id}>
                        <span>{input.filename}</span>
                        <Button
                          tone="quiet"
                          size="sm"
                          disabled={busy}
                          onClick={() =>
                            void detachDraftInput.mutateAsync({
                              runId: validDraft.id,
                              inputId: input.id,
                            })
                          }
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                {defaultCanalystInput && !canalyst && (
                  <div
                    className="agent-field-wide agent-draft-inputs"
                    role="status"
                  >
                    <span>Using existing company Canalyst workbook</span>
                    <div>
                      <span>{defaultCanalystInput.filename}</span>
                      {researchEnabled && (
                        <Button
                          tone="quiet"
                          size="sm"
                          disabled={busy}
                          onClick={() =>
                            setExcludedCanalystInputId(defaultCanalystInput.id)
                          }
                        >
                          Run without Canalyst
                        </Button>
                      )}
                    </div>
                  </div>
                )}
                <div className="agent-field-wide agent-evidence-section">
                  <div className="agent-evidence-heading">
                    <strong>Evidence</strong>
                    <span>
                      Add files you would like incorporated into this model.
                    </span>
                  </div>
                  <div className="agent-evidence-grid">
                    <FileUpload
                      label={
                        (editingDraft &&
                          validDraft?.inputs.some(
                            (input) => input.role === "canalyst",
                          )) ||
                        (!editingDraft && defaultCanalystInput !== null)
                          ? "Replace Canalyst workbook"
                          : researchEnabled
                            ? "Canalyst workbook · optional"
                            : "Canalyst workbook · required"
                      }
                      helperText={
                        defaultCanalystInput
                          ? "Upload a replacement Canalyst .xlsx export, or keep the existing company workbook shown above."
                          : researchEnabled
                            ? "Canalyst .xlsx export only, or leave it empty to launch a Research run. Add Daloopa, broker, and other models as supporting evidence."
                            : "Upload the company's Canalyst .xlsx export. Add Daloopa, broker, and other models as supporting evidence."
                      }
                      accept=".xlsx"
                      disabled={busy}
                      clearable
                      onFilesChange={(files) => setCanalyst(files[0] ?? null)}
                    />
                    <FileUpload
                      label={
                        editingDraft
                          ? "Add supporting evidence"
                          : "Supporting evidence · optional"
                      }
                      helperText="Upload additional files or research, including Daloopa or broker models, you would like the system to use when creating the model."
                      accept={accepts}
                      multiple
                      disabled={busy}
                      clearable
                      onFilesChange={setDocuments}
                    />
                  </div>
                </div>
                {canalyst ? (
                  <div className="agent-field-wide agent-input-metadata">
                    <strong>{canalyst.name} metadata</strong>
                    <label>
                      <span>Description · optional</span>
                      <TextInput
                        value={metadataFor("canalyst", 0, canalyst).description}
                        onChange={(event) =>
                          updateMetadata(
                            "canalyst",
                            0,
                            canalyst,
                            "description",
                            event.target.value,
                          )
                        }
                      />
                    </label>
                    <label>
                      <span>Source URL · optional</span>
                      <TextInput
                        type="url"
                        value={metadataFor("canalyst", 0, canalyst).sourceUrl}
                        onChange={(event) =>
                          updateMetadata(
                            "canalyst",
                            0,
                            canalyst,
                            "sourceUrl",
                            event.target.value,
                          )
                        }
                      />
                    </label>
                    <label>
                      <span>Release date · optional</span>
                      <TextInput
                        type="date"
                        value={metadataFor("canalyst", 0, canalyst).releaseDate}
                        onChange={(event) =>
                          updateMetadata(
                            "canalyst",
                            0,
                            canalyst,
                            "releaseDate",
                            event.target.value,
                          )
                        }
                      />
                    </label>
                  </div>
                ) : null}
                {documents.map((file, index) => (
                  <div
                    className="agent-field-wide agent-input-metadata"
                    key={metadataKey("other_document", index, file)}
                  >
                    <strong>{file.name} metadata</strong>
                    <label>
                      <span>Description · optional</span>
                      <TextInput
                        value={
                          metadataFor("other_document", index, file).description
                        }
                        onChange={(event) =>
                          updateMetadata(
                            "other_document",
                            index,
                            file,
                            "description",
                            event.target.value,
                          )
                        }
                      />
                    </label>
                    <label>
                      <span>Source URL · optional</span>
                      <TextInput
                        type="url"
                        value={
                          metadataFor("other_document", index, file).sourceUrl
                        }
                        onChange={(event) =>
                          updateMetadata(
                            "other_document",
                            index,
                            file,
                            "sourceUrl",
                            event.target.value,
                          )
                        }
                      />
                    </label>
                    <label>
                      <span>Release date · optional</span>
                      <TextInput
                        type="date"
                        value={
                          metadataFor("other_document", index, file).releaseDate
                        }
                        onChange={(event) =>
                          updateMetadata(
                            "other_document",
                            index,
                            file,
                            "releaseDate",
                            event.target.value,
                          )
                        }
                      />
                    </label>
                  </div>
                ))}
              </div>
            </fieldset>
          )}
        </section>
      </div>

      <footer className="agent-launch-footer">
        {modal && onClose && (
          <Button tone="quiet" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
        )}
        {editingDraft && (
          <Button
            tone="secondary"
            disabled={
              busy ||
              !draftChanged ||
              !validDraft ||
              !iterationFieldsReady ||
              !startDateValid
            }
            onClick={() => void saveDraft()}
          >
            {updateDraft.isPending ? "Saving…" : "Save changes"}
          </Button>
        )}
        <Button
          tone="primary"
          disabled={
            busy ||
            !companyId ||
            !iterationFieldsReady ||
            !startDateValid ||
            (!editingDraft && !newRunHasCanalyst && !researchEnabled) ||
            (editingDraft && (!validDraft || !draftCanLaunch))
          }
          onClick={() => void submit()}
        >
          {launchLabel}
        </Button>
      </footer>
      {creatingCompany && (
        <CreateCompanyModal
          onClose={() => setCreatingCompany(false)}
          onCreated={(company) => {
            setSelectedCompanyId(company.id);
            setCompanySelectionCleared(false);
            setCompanySearch("");
            setStartingPoint("fresh");
            setBaseModelVersionId("");
            setIterationMessage("");
            autoPrefilledBaseModelId.current = "";
            resetFiles();
          }}
        />
      )}
    </div>
  );
}
